import { TZDate } from '@date-fns/tz';

/**
 * Timezone-aware bridge between a **UTC instant** (ISO 8601, e.g.
 * `2026-05-01T21:00:00.000Z`) and the **wall-clock parts** a human edits in a
 * given IANA `timeZone` (e.g. `America/Sao_Paulo`).
 *
 * The contract: the value on the wire / in the DB is always a UTC instant; the
 * UI shows and edits it in a FIXED `timeZone` so the displayed time is the same
 * for every viewer regardless of their machine's local timezone. Conversion math
 * is delegated to `@date-fns/tz`'s `TZDate` (DST-correct).
 */

export interface ZonedParts {
  /** `yyyy-MM-dd` in the target timezone (empty when the instant is unset). */
  date: string;
  /** `HH:mm` (24h) in the target timezone (empty when the instant is unset). */
  time: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** UTC ISO instant → date/time parts as wall-clock in `timeZone`. */
export function utcIsoToZonedParts(iso: string | null | undefined, timeZone: string): ZonedParts {
  if (!iso) return { date: '', time: '' };
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return { date: '', time: '' };
  // A TZDate built from an instant exposes local getters in `timeZone`.
  const z = new TZDate(instant, timeZone);
  return {
    date: `${z.getFullYear()}-${pad2(z.getMonth() + 1)}-${pad2(z.getDate())}`,
    time: `${pad2(z.getHours())}:${pad2(z.getMinutes())}`,
  };
}

/**
 * Wall-clock parts (interpreted in `timeZone`) → UTC ISO instant. Returns `''`
 * when `date` is missing. A missing `time` defaults to midnight (`00:00`) in the
 * target timezone — the picker only emits once a date exists.
 */
export function zonedPartsToUtcIso(date: string, time: string, timeZone: string): string {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!dm) return '';
  const year = Number(dm[1]);
  const month = Number(dm[2]);
  const day = Number(dm[3]);
  const tm = /^(\d{1,2}):(\d{2})$/.exec(time || '00:00');
  const hours = tm ? Number(tm[1]) : 0;
  const minutes = tm ? Number(tm[2]) : 0;
  if (!year || !month || !day) return '';
  // Build the instant from wall-clock parts INTERPRETED in `timeZone`; the
  // resulting `.toISOString()` is the corresponding UTC instant (DST-aware).
  const z = new TZDate(year, month - 1, day, hours, minutes, 0, 0, timeZone);
  const ms = z.getTime();
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toISOString();
}
